import { gql } from 'graphql-request';

import { AI_VOICE_QUERY_FRAGMENT } from './ai-voice-gql';
import {
  BASIC_TEXT_BRANDING_QUERY,
  KARAOKE_TEXT_BRANDING_QUERY,
  SHOWCASE_BRANDING_QUERY,
} from './branding-gql';
import { COMPOSITION_TEMPLATE_QUERY_FRAGMENT } from './compositions-gql';
import { LANGUAGE_QUERY_FRAGMENT } from './language-gql';
import {
  MEDIA_QUERY_FRAGMENT,
  SONG_QUERY_FRAGMENT,
  VIDEO_FILE_GQL_FRAGMENT,
} from './media-gql';
import { CUSTOMER_TYPE_QUERY_FRAGMENT } from './misc-gql';
import {
  REFERENCING_CAPTION,
  REFERENCING_SHARED_CONTENT,
} from './shared-content-gql';
import { TRANSLATION_HANDLER_QUERY_FRAGMENT } from './translation-handler-gql';

import type { RequestDocument } from 'graphql-request';

export const SHOWCASE_QUERY: RequestDocument = `query getShowcase($slug: String!) {
  showcase(filter: {slug: {eq: $slug}}) {
    id
    title
    organization
    slug
    logo {
      url
    }
    mainImage {
      url
    }
    stories {
      id
      originalVideo {
        url
      }
      useAws
    }
    customerType ${CUSTOMER_TYPE_QUERY_FRAGMENT}
    organizationArtifacts ${MEDIA_QUERY_FRAGMENT}
    organizationLogos ${MEDIA_QUERY_FRAGMENT}
    organizationArtifactsVideo ${VIDEO_FILE_GQL_FRAGMENT}
    organizationAudios ${SONG_QUERY_FRAGMENT}
    lowerThirdTextBranding ${BASIC_TEXT_BRANDING_QUERY}
    karaokeTextBranding ${KARAOKE_TEXT_BRANDING_QUERY}
    compositionsBranding ${COMPOSITION_TEMPLATE_QUERY_FRAGMENT}
    quoteCardsBranding ${COMPOSITION_TEMPLATE_QUERY_FRAGMENT}
    lastUsedTemplates
    branding ${SHOWCASE_BRANDING_QUERY}
    allowLangSelectAtUserStoryUpload
    defaultLanguage
    defaultVideoTemplate { id }
    defaultPullQuoteTemplate { id }
    folderStructure
    paymentFailed
  }
}`;

// Shared selection for the secondary translation-fields queries below.
// Epic 5 / T36 will add localization fields — extend here.
const TRANSLATION_FIELDS_SELECTION = `
  translationsEnabled
  supportedLanguages ${LANGUAGE_QUERY_FRAGMENT}
  translationHandlerOverride ${TRANSLATION_HANDLER_QUERY_FRAGMENT}
`;

// Secondary query for translation-related showcase fields. Kept separate so
// schema drift (e.g., migration not yet applied in the target environment)
// does NOT block the primary showcase load. Callers run this inside a
// try/catch and default to empty values on failure.
export const SHOWCASE_TRANSLATION_FIELDS_QUERY: RequestDocument = `query getShowcaseTranslationFields($slug: String!) {
  showcase(filter: {slug: {eq: $slug}}) {
    id
    ${TRANSLATION_FIELDS_SELECTION}
  }
}`;

// Shared selection for the secondary dubbing-fields queries below. Mirrors
// TRANSLATION_FIELDS_SELECTION: the per-workspace dubbing activation (DE3).
const DUBBING_FIELDS_SELECTION = `
  dubbingEnabled
  activatedAiVoices ${AI_VOICE_QUERY_FRAGMENT}
`;

// Secondary query for dubbing-related showcase fields. Kept separate (like the
// translation fields above) so schema drift — the migration not yet applied in
// the target environment — does NOT block the primary showcase load. Callers
// run this inside a try/catch and default to empty values on failure.
export const SHOWCASE_DUBBING_FIELDS_QUERY: RequestDocument = `query getShowcaseDubbingFields($slug: String!) {
  showcase(filter: {slug: {eq: $slug}}) {
    id
    ${DUBBING_FIELDS_SELECTION}
  }
}`;

export const DASHBOARD_ALBUM_QUERY: RequestDocument = gql`
  query getAlbum($id: ItemId) {
    showcase(filter: { id: { eq: $id } }) {
      id
      title
      stories {
        id
        title
        aiResponse
        byExternalUser
        externalUploadStatus
        transcription {
          jobStatus
        }
        uploadLog {
          id
          initialUpload
          ffmpegProcessing
          combineResults
          extractThumbnailsFromLowRes
          handleFailure
        }
        storyTeller {
          name
        }
        speakers {
          id
          name
          title
          headshots {
            id
            url
          }
        }
        _publishedAt
        _createdAt
        thumbnail {
          url
        }
        clipThemesJson
        useAws
        savedBlog
        savedSocialPost
        shareableImages {
          id
          imagefile {
            url
          }
          quote
          metadata
          _updatedAt
        }
        originalVideo {
          video {
            duration
            thumbnailUrl
            muxPlaybackId
          }
        }
        finalVideo {
          slug
          hash
          thumbnail {
            url
          }
          videoFilePrimary {
            url
            video {
              duration
              muxPlaybackId
            }
          }
        }
        otherVideos {
          id
          slug
          hash
          title
          isHidden
          clipJson
          reviewStatus
          assignee
          lastActionJson
          lastPublishJson
          thumbnail {
            url
          }
          transcriptionText
          videoFilePrimary {
            url
            video {
              duration
              thumbnailUrl
              muxPlaybackId
            }
          }
          aspectRatio
          associatedVideos {
            id
            slug
            hash
            aspectRatio
            lastPublishJson
            videoFilePrimary {
              url
              video {
                thumbnailUrl
                muxPlaybackId
              }
            }
          }
          _publishedAt
          _updatedAt
        }
      }
      folderStructure
    }
  }
`;

// Deferred companion to DASHBOARD_ALBUM_QUERY: story-level shared contents
// (Story Manager's platform/analytics columns). _allReferencing* fields are
// inverse-reference lookups — the most expensive part of the album query — so
// they load separately after first paint instead of blocking the story list.
export const ALBUM_SHARED_CONTENTS_QUERY: RequestDocument = gql`
  query getAlbumSharedContents($id: ItemId) {
    showcase(filter: { id: { eq: $id } }) {
      id
      stories {
        id
        _allReferencingSharedContents {
          video {
            title
          }
          shareableContent {
            title
          }
          story {
            id
          }
          platform
          likes
          comments
          shares
          views
        }
      }
    }
  }
`;

export const ALBUM_QUERY: RequestDocument = `query getAlbum($id: ItemId) {
  showcase(filter: {id: {eq: $id}}) {
    id
    title
    stories {
      id
      title
      aiResponse
      byExternalUser
      externalUploadStatus
      transcription {
        jobStatus
      }
      uploadLog {
        id
        initialUpload
        submitToAuphonic
        checkAuphonicProductionStatus
        transferFilesToProductionBucket
        ffmpegProcessing
        compressVideo
        generateWaveform
        awsTranscribe
        checkAwsTranscribeStatus
        revaiTranscribe
        checkRevaiTranscribeStatus
        combineResults
        extractThumbnailsFromLowRes
        handleFailure
      }
      storyTeller {
        name
      }
      _allReferencingSharedContents
      ${REFERENCING_SHARED_CONTENT}
      _publishedAt
      thumbnail {
        url
      }
      useAws
      originalVideo
        ${VIDEO_FILE_GQL_FRAGMENT}
      finalVideo {
        thumbnail {
          url
        }
        videoFilePrimary
        ${VIDEO_FILE_GQL_FRAGMENT}
      }
      otherVideos {
        id
        title
        videoStatus
        sourcePlatform
        isClientReady
        isHidden
        clipJson
        reviewStatus
        assignee
        lastActionJson
        videoJson
        thumbnail {
          url
        }
        shareableImageId
        videoFilePrimary
          ${VIDEO_FILE_GQL_FRAGMENT}
        slug
        hash
        aspectRatio
        aiGeneratedContent {
          id
          prompt
          generatedContent
        }
        associatedVideos {
          id
          aspectRatio
          videoStatus
          sourcePlatform
          isClientReady
          isHidden
          clipJson
          lastActionJson
          thumbnail {
            url
          }
          transcriptionText
          shareableImageId
          videoFilePrimary
            ${VIDEO_FILE_GQL_FRAGMENT}
        }
        _allReferencingSharedContents
        ${REFERENCING_SHARED_CONTENT}
        _allReferencingCaptions ${REFERENCING_CAPTION}
        _publishedAt
      }
    }
    customerType ${CUSTOMER_TYPE_QUERY_FRAGMENT}
    organizationArtifacts ${MEDIA_QUERY_FRAGMENT}
    organizationArtifactsVideo ${MEDIA_QUERY_FRAGMENT}
    organizationLogos ${MEDIA_QUERY_FRAGMENT}
    organizationAudios ${SONG_QUERY_FRAGMENT}
    contributors {
      id
      name
      email
    }
    logo {
      url
    }
    mainImage {
      url
    }
    lowerThirdTextBranding ${BASIC_TEXT_BRANDING_QUERY}
    karaokeTextBranding ${KARAOKE_TEXT_BRANDING_QUERY}
    compositionsBranding ${COMPOSITION_TEMPLATE_QUERY_FRAGMENT}
    quoteCardsBranding ${COMPOSITION_TEMPLATE_QUERY_FRAGMENT}
    lastUsedTemplates
    branding ${SHOWCASE_BRANDING_QUERY}
    allowLangSelectAtUserStoryUpload
    defaultLanguage
    defaultVideoTemplate { id }
    defaultPullQuoteTemplate { id }
    folderStructure
    paymentFailed
  }
}`;

// Id-based counterpart to SHOWCASE_TRANSLATION_FIELDS_QUERY. Same resilience
// contract: run inside a try/catch, default to empty values on failure so
// the primary album fetch is not coupled to the translation schema state.
export const ALBUM_TRANSLATION_FIELDS_QUERY: RequestDocument = `query getAlbumTranslationFields($id: ItemId) {
  showcase(filter: {id: {eq: $id}}) {
    id
    ${TRANSLATION_FIELDS_SELECTION}
  }
}`;

// Id-based counterpart to SHOWCASE_DUBBING_FIELDS_QUERY. Same resilience
// contract as the translation counterpart above.
export const ALBUM_DUBBING_FIELDS_QUERY: RequestDocument = `query getAlbumDubbingFields($id: ItemId) {
  showcase(filter: {id: {eq: $id}}) {
    id
    ${DUBBING_FIELDS_SELECTION}
  }
}`;

export const ALBUM_STORY_IDS_QUERY: RequestDocument = `query getAlbum($id: ItemId) {
  showcase(filter: {id: {eq: $id}}) {
    stories {
      id
    }
  }
}`;
